import React, { useState } from 'react'
import {
  useGetProjectEmployeePerformanceQuery,
  useGetEmployeeListQuery,
  ProjectEmployeePerformance,
} from '@/redux/service/project'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'
import ReusableDateRangePicker, {
  DateRange,
} from '@/components/ReusableDateRangePicker'
import { formatSeconds } from '@/lib/utils'
import { Skeleton } from './ui/skeleton'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import { Users } from 'lucide-react'
import { format } from 'date-fns'

interface ProjectTeamPerformanceProps {
  projectId: number
}

/** Deterministic hue from a string so each member gets a unique avatar color */
const nameToHue = (name: string) => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % 360
}

/** Helper to format currency values. Returns em-dash '—' if value is 0, null, or undefined. */
const formatCurrencyWithDash = (
  value: number | string | null | undefined
): string => {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (isNaN(num) || num === 0) return '—'
  return `$${num.toFixed(2)}`
}

const ProjectTeamPerformance: React.FC<ProjectTeamPerformanceProps> = ({
  projectId,
}) => {
  const { hasPageAccess } = useCheckPermission()
  const [employee, setEmployee] = useState('all')
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  })

  const { data: employeeList, isLoading: employeeListLoading } =
    useGetEmployeeListQuery()

  const { data, isLoading, isError } = useGetProjectEmployeePerformanceQuery(
    {
      id: projectId,
      ...(employee !== 'all' ? { employee } : {}),
      ...(dateRange.from
        ? { startDate: format(dateRange.from, 'yyyy-MM-dd') }
        : {}),
      ...(dateRange.to ? { endDate: format(dateRange.to, 'yyyy-MM-dd') } : {}),
    },
    { skip: !projectId }
  )

  const filterBar = (
    <div className='flex flex-col sm:flex-row gap-3 px-4 py-3 bg-muted/40 border-b border-border'>
      <Select value={employee} onValueChange={setEmployee}>
        <SelectTrigger className='h-9 w-full sm:w-[220px] border-border/50 text-foreground rounded-lg'>
          <SelectValue placeholder='All Employees' />
        </SelectTrigger>
        <SelectContent className='bg-popover border-border/50'>
          <SelectItem value='all'>All Employees</SelectItem>
          {employeeListLoading ? (
            <SelectItem value='loading' disabled>
              Loading...
            </SelectItem>
          ) : (
            (employeeList?.data ?? []).map(user => (
              <SelectItem key={user.user_id} value={user.user_name}>
                {user.user_name}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      <ReusableDateRangePicker
        value={dateRange}
        onSelect={setDateRange}
        className='w-full sm:w-[280px] h-9'
      />
    </div>
  )

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className='border border-border rounded-xl bg-card shadow-sm overflow-hidden'>
        {filterBar}
        <div className='divide-y divide-border/50'>
          {[...Array(4)].map((_, i) => (
            <div key={i} className='flex items-center gap-4 px-4 py-3'>
              <Skeleton className='h-8 w-8 rounded-full shrink-0' />
              <Skeleton className='h-4 w-32 rounded' />
              <Skeleton className='h-4 w-16 rounded ml-auto' />
              <Skeleton className='h-4 w-16 rounded' />
              <Skeleton className='h-4 w-16 rounded' />
            </div>
          ))}
        </div>
      </div>
    )
  }

  /* ── Empty / Error ── */
  if (isError || !data?.success || !data.data.length) {
    return (
      <div className='border border-border rounded-xl bg-card shadow-sm overflow-hidden'>
        {filterBar}
        <div className='flex flex-col items-center justify-center py-12 gap-2'>
          <Users className='w-8 h-8 text-muted-foreground/30' />
          <p className='text-sm font-medium text-muted-foreground'>
            No team performance data available
          </p>
          <p className='text-xs text-muted-foreground/60'>
            Data will appear once time entries are logged
          </p>
        </div>
      </div>
    )
  }

  const totals = data.data.reduce(
    (acc, member) => {
      acc.estHours += Number(member.milestone_time_estimate_seconds || 0)
      acc.totalHours += Number(member.total_duration_seconds || 0)
      acc.billableHours += Number(member.billable_duration_seconds || 0)
      acc.nonBillableHours += Number(member.non_billable_duration_seconds || 0)
      acc.totalCost += Number(member.total_employee_cost || 0)
      acc.billableCost += Number(member.total_billable_employee_cost || 0)
      acc.nonBillableCost += Number(member.calculated_non_billable_cost || 0)
      acc.totalRevenue += Number(member.total_project_cost || 0)
      acc.billableRevenue += Number(member.total_billable_project_cost || 0)
      return acc
    },
    {
      estHours: 0,
      totalHours: 0,
      billableHours: 0,
      nonBillableHours: 0,
      totalCost: 0,
      billableCost: 0,
      nonBillableCost: 0,
      totalRevenue: 0,
      billableRevenue: 0,
    }
  )

  const thClass =
    'text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'

  return (
    <div className='border border-border rounded-sm bg-card shadow-sm overflow-hidden'>
      {filterBar}
      <div className='overflow-x-auto'>
        <Table className='border-none'>
          <TableHeader>
            <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
              <TableHead className={`${thClass} py-3`}>Team Member</TableHead>
              <TableHead className={`${thClass} text-center`}>
                Est. Hours
              </TableHead>
              <TableHead className={`${thClass} text-center`}>
                Total Hours
              </TableHead>
              <TableHead className={`${thClass} text-center`}>
                Billable
              </TableHead>
              <TableHead className={`${thClass} text-center`}>
                Non-Billable
              </TableHead>

              {hasPageAccess('finance') && (
                <>
                  <TableHead className={`${thClass} text-right`}>
                    Total Cost
                  </TableHead>
                  <TableHead className={`${thClass} text-right`}>
                    Billable Cost
                  </TableHead>
                  <TableHead className={`${thClass} text-right`}>
                    Non-Bill. Cost
                  </TableHead>
                  <TableHead className={`${thClass} text-right`}>
                    Total Revenue
                  </TableHead>
                  <TableHead className={`${thClass} text-right`}>
                    Bill. Revenue
                  </TableHead>
                </>
              )}

              {hasPageAccess('mini_finance') && (
                <>
                  <TableHead className={`${thClass} text-right`}>
                    Total Revenue
                  </TableHead>
                  <TableHead className={`${thClass} text-right`}>
                    Bill. Revenue
                  </TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.data.map(
              (member: ProjectEmployeePerformance, idx: number) => {
                const hue = nameToHue(member?.user_name ?? '')
                return (
                  <TableRow
                    key={`${member?.clickup_id}-${idx}`}
                    className='border-b border-border/50 hover:bg-accent/30 transition-colors duration-150 group'
                  >
                    <TableCell className='px-4 py-3'>
                      <div className='flex items-center gap-2.5'>
                        <div
                          className='w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 border'
                          style={{
                            backgroundColor: `hsl(${hue} 55% 90%)`,
                            color: `hsl(${hue} 55% 35%)`,
                            borderColor: `hsl(${hue} 55% 80%)`,
                          }}
                        >
                          {member?.user_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <span className='text-sm font-medium truncate group-hover:text-primary transition-colors'>
                          {member?.user_name}
                        </span>
                      </div>
                    </TableCell>

                    {/* Estimated Hours */}
                    <TableCell className='px-4 py-3 text-center'>
                      <span className='text-sm font-semibold tabular-nums'>
                        {formatSeconds(
                          Number(member?.milestone_time_estimate_seconds || 0)
                        )}
                      </span>
                    </TableCell>

                    {/* Total Hours */}
                    <TableCell className='px-4 py-3 text-center'>
                      <span className='text-sm tabular-nums'>
                        {formatSeconds(Number(member?.total_duration_seconds))}
                      </span>
                    </TableCell>

                    {/* Billable Hours */}
                    <TableCell className='px-4 py-3 text-center'>
                      <span className='inline-flex items-center px-2 py-0.5   text-emerald-600 dark:text-emerald-400 text-xs font-medium tabular-nums '>
                        {formatSeconds(
                          Number(member?.billable_duration_seconds || 0)
                        )}
                      </span>
                    </TableCell>

                    {/* Non-Billable Hours */}
                    <TableCell className='px-4 py-3 text-center'>
                      <span className='inline-flex items-center px-2 py-0.5 rounded-md  text-muted-foreground text-xs font-medium tabular-nums'>
                        {formatSeconds(
                          Number(member?.non_billable_duration_seconds || 0)
                        )}
                      </span>
                    </TableCell>

                    {hasPageAccess('finance') && (
                      <>
                        <TableCell className='px-4 py-3 text-right'>
                          <span className='text-sm font-semibold tabular-nums'>
                            {formatCurrencyWithDash(
                              member?.total_employee_cost
                            )}
                          </span>
                        </TableCell>
                        <TableCell className='px-4 py-3 text-right'>
                          <span className='text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 tracking-wide'>
                            {formatCurrencyWithDash(
                              member?.total_billable_employee_cost
                            )}
                          </span>
                        </TableCell>
                        <TableCell className='px-4 py-3 text-right'>
                          <span className='text-sm font-semibold tabular-nums text-muted-foreground'>
                            {formatCurrencyWithDash(
                              member?.calculated_non_billable_cost
                            )}
                          </span>
                        </TableCell>
                        <TableCell className='px-4 py-3 text-right'>
                          <span className='text-sm font-semibold tabular-nums'>
                            {formatCurrencyWithDash(member?.total_project_cost)}
                          </span>
                        </TableCell>
                        <TableCell className='px-4 py-3 text-right'>
                          <span className='text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 tracking-wide'>
                            {formatCurrencyWithDash(
                              member?.total_billable_project_cost
                            )}
                          </span>
                        </TableCell>
                      </>
                    )}

                    {hasPageAccess('mini_finance') && (
                      <>
                        <TableCell className='px-4 py-3 text-right'>
                          <span className='text-sm font-semibold tabular-nums'>
                            {formatCurrencyWithDash(member.total_project_cost)}
                          </span>
                        </TableCell>
                        <TableCell className='px-4 py-3 text-right'>
                          <span className='text-sm font-semibold tabular-nums text-green-600 dark:text-green-400'>
                            {formatCurrencyWithDash(
                              member.total_billable_project_cost
                            )}
                          </span>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                )
              }
            )}
          </TableBody>

          <TableFooter className='bg-muted/50 font-bold border-t-2 border-border'>
            <TableRow>
              <TableCell className='px-4 py-3 text-sm font-bold'>
                Total
              </TableCell>

              <TableCell className='px-4 py-3 text-center text-sm font-bold tabular-nums'>
                {formatSeconds(totals.estHours)}
              </TableCell>

              <TableCell className='px-4 py-3 text-center text-sm font-bold tabular-nums'>
                {formatSeconds(totals.totalHours)}
              </TableCell>

              <TableCell className='px-4 py-3 text-center text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400'>
                {formatSeconds(totals.billableHours)}
              </TableCell>

              <TableCell className='px-4 py-3 text-center text-sm font-bold tabular-nums text-muted-foreground'>
                {formatSeconds(totals.nonBillableHours)}
              </TableCell>

              {hasPageAccess('finance') && (
                <>
                  <TableCell className='px-4 py-3 text-right text-sm font-bold tabular-nums'>
                    {formatCurrencyWithDash(totals.totalCost)}
                  </TableCell>
                  <TableCell className='px-4 py-3 text-right text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400'>
                    {formatCurrencyWithDash(totals.billableCost)}
                  </TableCell>
                  <TableCell className='px-4 py-3 text-right text-sm font-bold tabular-nums text-muted-foreground'>
                    {formatCurrencyWithDash(totals.nonBillableCost)}
                  </TableCell>
                  <TableCell className='px-4 py-3 text-right text-sm font-bold tabular-nums'>
                    {formatCurrencyWithDash(totals.totalRevenue)}
                  </TableCell>
                  <TableCell className='px-4 py-3 text-right text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400'>
                    {formatCurrencyWithDash(totals.billableRevenue)}
                  </TableCell>
                </>
              )}

              {hasPageAccess('mini_finance') && (
                <>
                  <TableCell className='px-4 py-3 text-right text-sm font-bold tabular-nums'>
                    {formatCurrencyWithDash(totals.totalRevenue)}
                  </TableCell>
                  <TableCell className='px-4 py-3 text-right text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400'>
                    {formatCurrencyWithDash(totals.billableRevenue)}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  )
}

export default ProjectTeamPerformance
